import { ChevronDownIcon } from '@mote/assets/icons'
import { isNumber } from '@mote/helpers'
import React from 'react'
import styled, { css } from 'styled-components'
import { VisuallyHidden } from '../visuallyHidden/visuallyHidden'
import {
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownItemText
} from './dropdown.components'
import {
  TRANSITION_DURATION,
  CLICK_TRANSITION_DURATION,
  HOVER_TRANSITION_DURATION,
  useDropdown,
  TRIGGER
} from './useDropdown'

export const Dropdown = ({
  className,
  triggerElement,
  triggerElementContent,
  showChevron = true,
  ...rest
}) => {
  const { children, menuProps, triggerProps, isOpen, trigger } =
    useDropdown(rest)
  const isHover = React.useMemo(() => trigger === TRIGGER.hover, [trigger])

  const chevron = React.useMemo(() => {
    return showChevron ? (
      <StyledChevronDownIcon
        width='0.75em'
        height='0.75em'
        color='currentColor'
        aria-hidden={true}
        focusable={false}
        isOpen={isOpen}
        isHover={isHover}
      />
    ) : null
  }, [isOpen, isHover, showChevron])

  const triggerBtn = React.useMemo(() => {
    const screenReaderText = triggerProps['aria-label'] ? (
      <VisuallyHidden>{triggerProps['aria-label']}</VisuallyHidden>
    ) : null

    return !triggerElement ? (
      <TriggerElementWrapper {...triggerProps}>
        {screenReaderText}
        {triggerElementContent}
        {chevron}
      </TriggerElementWrapper>
    ) : (
      React.cloneElement(
        triggerElement,
        { ...triggerProps, ...triggerElement.props, isHover },
        <>
          {screenReaderText}
          {triggerElement.props.children}
          {chevron}
        </>
      )
    )
  }, [isHover, triggerElement, triggerElementContent, triggerProps, chevron])

  return (
    <Root className={className}>
      {triggerBtn}
      <StyledDropdown {...menuProps} isHover={isHover}>
        {children}
      </StyledDropdown>
    </Root>
  )
}

Dropdown.Item = DropdownItem
Dropdown.Header = DropdownHeader
Dropdown.ItemText = DropdownItemText
Dropdown.Divider = DropdownDivider

const Root = styled.div`
  display: inline-block;
  position: relative;
  max-width: 100%;
`

const StyledDropdown = styled.ul`
  position: ${({ inline }) => (inline ? 'relative' : 'absolute')};
  display: flex;
  border: 1px solid rgba(0, 0, 0, 0.1);
  width: ${({ width }) => (isNumber(width) ? `${width}px` : width)};
  height: ${({ height }) => (isNumber(height) ? `${height}px` : height)};
  border-radius: 8px;
  box-shadow: 0px 1px 3px rgba(113, 101, 129, 0.04),
    0px 3px 14px rgba(113, 101, 129, 0.08),
    0px 8px 32px rgba(113, 101, 129, 0.1);
  z-index: 40;
  overflow: hidden;
  margin: 0;
  opacity: 0;
  outline: none;
  visibility: hidden;
  transform: ${({ transitionType }) =>
    transitionType === 'scale' ? 'scale(0.9)' : null};
  transition-property: opacity, visibility, transform, box-shadow;
  transition-duration: ${({ isHover }) =>
    isHover ? HOVER_TRANSITION_DURATION : CLICK_TRANSITION_DURATION}ms;
  transition-timing-function: ease-in;

  &:focus {
    outline: none;
  }

  ${({ variant }) => css`
    background: ${variant === 'light' ? '#ffffff' : '#212228'};

    li {
      color: ${variant === 'light' ? '#212228' : '#ffffff'};

      &:focus,
      &:hover {
        background-color: ${variant === 'light' ? '#f6f6f9' : '#3B3B42'};
      }
    }
  `};

  ${({ orientation }) => {
    switch (orientation) {
      case 'vertical':
        return css`
          flex-direction: column;
          padding: 8px 0;
          overflow-y: auto;
        `
      case 'horizontal':
        return css`
          flex-direction: row;
          padding: 0 8px;
          overflow-x: auto;
        `
    }
  }};

  ${({ placement, inline }) => {
    if (inline) return ''

    switch (placement) {
      case 'bottom-left':
        return css`
          left: 0px;
          margin-top: 10px;
        `
      case 'bottom-right':
        return css`
          right: 0px;
          margin-top: 10px;
        `
      case 'bottom-center':
        return css`
          left: 50%;
          margin-top: 10px;
          transform: translateX(-50%);
        `
      case 'top-center':
        return css`
          left: 50%;
          bottom: 100%;
          margin-bottom: 10px;
          transform: translateX(-50%);
        `
      case 'top-left':
        return css`
          bottom: 100%;
          left: 0px;
          margin-bottom: 10px;
        `
      case 'top-right':
        return css`
          bottom: 100%;
          right: 0px;
          margin-bottom: 10px;
        `
    }
  }};

  ${({ placement, transitionType, inline }) => {
    if (inline) return ''

    switch (placement) {
      case 'bottom-left': {
        if (transitionType === 'scale')
          return css`
            transform-origin: top left;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(10px);
          `
      }
      case 'bottom-right': {
        if (transitionType === 'scale')
          return css`
            transform-origin: top right;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(10px);
          `
      }
      case 'bottom-center': {
        if (transitionType === 'scale')
          return css`
            transform-origin: top center;
            transform: translateX(-50%) scale(0.9);
          `

        if (transitionType === 'slide')
          return css`
            transform: translate(-50%, 10px);
          `
      }
      case 'top-center': {
        if (transitionType === 'scale')
          return css`
            transform-origin: bottom center;
            transform: translateX(-50%) scale(0.9);
          `

        if (transitionType === 'slide')
          return css`
            transform: translate(-50%, -10px);
          `
      }
      case 'top-left': {
        if (transitionType === 'scale')
          return css`
            transform-origin: bottom left;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(-10px);
          `
      }
      case 'top-right': {
        if (transitionType === 'scale')
          return css`
            transform-origin: bottom right;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(-10px);
          `
      }
    }
  }};

  ${({ inline }) =>
    inline &&
    css`
      height: unset;
      max-height: 0;
      border: none;
      border-radius: 0;
      box-shadow: none;
      transition: all 250ms ease-in-out;

      &:focus {
        box-shadow: 0 0 0 3px rgba(172, 10, 231, 0.4);
      }
    `};

  ${({ inline, isOpen }) =>
    inline &&
    !isOpen &&
    css`
      margin: 0 !important;
      padding: 0 !important;
    `};

  ${({ isOpen, height }) =>
    isOpen &&
    css`
      max-height: ${isOpen ? (height ? `${height}px` : '1000px') : 0};
      opacity: 1;
      visibility: visible;
    `};

  ${({ isOpen, transitionType, placement }) => {
    if (!isOpen) return

    switch (placement) {
      case 'bottom-left':
      case 'bottom-right':
      case 'top-left':
      case 'top-right': {
        if (transitionType === 'scale')
          return css`
            transform: scale(1);
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(0);
          `
      }
      case 'bottom-center':
      case 'top-center': {
        if (transitionType === 'scale')
          return css`
            transform: translateX(-50%) scale(1);
          `

        if (transitionType === 'slide')
          return css`
            transform: translate(-50%, 0);
          `
      }
    }
  }};
`

const TriggerElementWrapper = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #25252d;
  font-size: 15px;
  font-weight: 400;
  line-height: 120%;
  border-radius: 4px;
  width: auto;
  cursor: pointer;
  appearance: none;
  user-select: none;
  white-space: nowrap;
  vertical-align: middle;
  border: 1px solid transparent;
  background: transparent;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition-property: all;
  transition-duration: ${({ isHover }) =>
    isHover ? HOVER_TRANSITION_DURATION : CLICK_TRANSITION_DURATION}ms;

  box-sizing: border-box;

  &:active {
    color: #9b09d1;
  }

  &:focus {
    box-shadow: 0 0 0 3px rgba(172, 10, 231, 0.4);
  }

  &:hover,
  &:focus {
    color: #ac0ae8;
    background: #f6f6f9;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    `};
`

const StyledChevronDownIcon = styled(ChevronDownIcon)`
  transition-property: transform;
  transition-duration: ${({ isHover }) =>
    isHover ? HOVER_TRANSITION_DURATION : CLICK_TRANSITION_DURATION}ms;

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(-180deg);
    `};
`
